import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { asArray } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth, hasRole } from "../../contexts/AuthContext";
import { FolderKanban, Newspaper, Sparkles, Building2, Users, Mail, Settings, Heart, Hash, MessageSquare, FileText, Globe2 } from "lucide-react";

export default function AdminDashboard() {
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const [counts, setCounts] = useState({ works: 0, news: 0, services: 0, clients: 0, contacts: 0, users: 0, values: 0, stats: 0 });

    useEffect(() => {
        const run = async () => {
            const next = { ...counts };
            try { next.works = asArray((await api.get("/admin/works")).data, "works").length; } catch {}
            try { next.news = asArray((await api.get("/admin/news")).data, "news").length; } catch {}
            try { next.services = asArray((await api.get("/admin/services")).data, "services").length; } catch {}
            try { next.clients = asArray((await api.get("/admin/clients")).data, "clients").length; } catch {}
            try { next.values = asArray((await api.get("/admin/values")).data, "values").length; } catch {}
            try { next.stats = asArray((await api.get("/admin/stats")).data, "stats").length; } catch {}
            if (hasRole(user, "super_admin")) {
                try { next.contacts = asArray((await api.get("/admin/contacts")).data, "contacts").length; } catch {}
                try { next.users = asArray((await api.get("/admin/users")).data, "users").length; } catch {}
            }
            setCounts(next);
        };
        run();
        // eslint-disable-next-line
    }, []);

    const cards = [
        { k: "works", to: "/admin/works", t: t("admin_works"), icon: FolderKanban, roles: ["editor"] },
        { k: "news", to: "/admin/news", t: t("admin_news"), icon: Newspaper, roles: ["news_editor"] },
        { k: "services", to: "/admin/services", t: t("admin_services"), icon: Sparkles, roles: ["editor"] },
        { k: "values", to: "/admin/values", t: t("admin_values"), icon: Heart, roles: ["editor"] },
        { k: "stats", to: "/admin/stats", t: t("admin_stats"), icon: Hash, roles: ["editor"] },
        { k: "clients", to: "/admin/clients", t: t("admin_clients"), icon: Building2, roles: ["editor"] },
        { k: "users", to: "/admin/users", t: t("admin_users"), icon: Users, roles: ["super_admin"] },
        { k: "contacts", to: "/admin/contacts", t: t("admin_contacts"), icon: Mail, roles: ["super_admin"] },
    ];

    const quickLinks = [
        { to: "/admin/settings", t: t("admin_site_settings"), icon: Settings, hint: lang === "ar" ? "الشعار، التواصل، السوشيال، SEO" : "Logo, contact, social, SEO" },
        { to: "/admin/welcome", t: t("admin_welcome"), icon: MessageSquare, hint: lang === "ar" ? "نص ترحيب المؤسس" : "Founder welcome message" },
        { to: "/admin/profile", t: t("admin_profile"), icon: FileText, hint: lang === "ar" ? "ملف PDF التعريفي" : "Company profile PDF" },
        { to: "/admin/brand/media", t: t("admin_global"), icon: Globe2, hint: lang === "ar" ? "الوصول العالمي" : "Global reach media" },
    ];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="heading text-4xl tracking-tight mb-2">
                {lang === "ar" ? `أهلاً، ${user.name}` : `Welcome, ${user.name}`}
            </h1>
            <p className="text-[#6B7280] mb-10">
                {lang === "ar" ? "ملخص سريع لمحتوى الموقع." : "Quick overview of your site content."}
            </p>

            {/* Quick access tiles */}
            {hasRole(user, "editor", "super_admin") && (
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-3">
                        {lang === "ar" ? "وصول سريع" : "Quick Access"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {quickLinks.map((q) => (
                            <Link key={q.to} to={q.to} className="bg-[#002951] text-white p-5 hover:bg-[#0060A7] transition-colors group" data-testid={`quick-${q.to.replace(/\//g, "-")}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <q.icon size={20} className="text-white/80" />
                                </div>
                                <div className="heading text-base font-bold">{q.t}</div>
                                <div className="text-xs text-white/60 mt-1">{q.hint}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-3">
                {lang === "ar" ? "المحتوى" : "Content"}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.filter((c) => hasRole(user, ...c.roles)).map((c) => (
                    <Link key={c.k} to={c.to} className="bg-white p-6 border border-black/5 hover:border-[#0060A7] transition-colors" data-testid={`stat-card-${c.k}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">{c.t}</span>
                            <c.icon size={18} className="text-[#0060A7]" />
                        </div>
                        <div className="heading text-5xl tracking-tighter mt-4">{counts[c.k]}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
